import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GridSectionContent, GridItem, CommunityPageSectionRow } from '@/types/cms';
import { Save, X, Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Expand, Minimize } from 'lucide-react';

interface GridSectionEditorProps {
  section: CommunityPageSectionRow;
  onSave: (updates: Partial<CommunityPageSectionRow>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Create a unique identifier for each grid item
interface GridItemWithId extends GridItem {
  id: string;
}

interface SortableGridItemProps {
  item: GridItemWithId;
  index: number;
  onItemChange: (index: number, field: string, value: string) => void;
  onRemove: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const SortableGridItem: React.FC<SortableGridItemProps> = ({ 
  item, 
  index, 
  onItemChange, 
  onRemove,
  isExpanded,
  onToggleExpand
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'opacity-50' : ''}>
      <Card className={`border transition-all duration-200 ${
        isExpanded ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200 bg-white'
      }`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleExpand}
                className="p-1 h-auto"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
              
              <div className="flex-1">
                <CardTitle className="text-sm font-medium">
                  {item.title || `Item ${index + 1}`}
                </CardTitle>
                {!isExpanded && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {item.description ? 
                      item.description.substring(0, 50) + (item.description.length > 50 ? '...' : '') 
                      : 'No description'
                    }
                  </p>
                )}
              </div>
              
              {!isExpanded && item.icon && (
                <div className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded">
                  {item.icon}
                </div>
              )}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onRemove}
              className="text-destructive hover:text-destructive ml-2"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="space-y-3 pt-0 border-t bg-white">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Title</Label>
                <Input
                  value={item.title || ''}
                  onChange={(e) => onItemChange(index, 'title', e.target.value)}
                  placeholder="Item title"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Icon (Lucide name)</Label>
                <Input
                  value={item.icon || ''}
                  onChange={(e) => onItemChange(index, 'icon', e.target.value)}
                  placeholder="e.g., Star, Users"
                  className="text-sm"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={item.description || ''}
                onChange={(e) => onItemChange(index, 'description', e.target.value)}
                placeholder="Item description"
                rows={2}
                className="text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Image URL (optional)</Label>
                <Input
                  value={item.image_url || ''}
                  onChange={(e) => onItemChange(index, 'image_url', e.target.value)}
                  placeholder="https://..."
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Link (optional)</Label>
                <Input
                  value={item.link || ''}
                  onChange={(e) => onItemChange(index, 'link', e.target.value)}
                  placeholder="/services"
                  className="text-sm"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

const GridSectionEditor: React.FC<GridSectionEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const content = section.content as GridSectionContent;
  
  // Transform items to include IDs for drag and drop
  const itemsWithIds = React.useMemo(() => {
    return (content.items || []).map((item, index) => ({
      ...item,
      id: `item-${index}-${Date.now()}`
    }));
  }, [content.items]);

  const [formData, setFormData] = useState({
    title: section.title || '',
    columns: content.columns || 2,
    card_style: content.card_style || 'default',
    items: itemsWithIds
  });

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const expandAllItems = () => {
    setExpandedItems(new Set(formData.items.map(item => item.id)));
  };

  const collapseAllItems = () => {
    setExpandedItems(new Set());
  };

  useEffect(() => {
    const newItemsWithIds = (content.items || []).map((item, index) => ({
      ...item,
      id: `item-${index}-${Date.now()}`
    }));
    
    setFormData({
      title: section.title || '',
      columns: content.columns || 2,
      card_style: content.card_style || 'default',
      items: newItemsWithIds
    });
  }, [section, content]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index: number, field: string, value: string) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    const newItem: GridItemWithId = {
      id: `item-${Date.now()}-${Math.random()}`,
      title: '',
      description: '',
      icon: '',
      image_url: '',
      link: ''
    };
    setFormData(prev => ({ 
      ...prev, 
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = formData.items.findIndex((item) => item.id === active.id);
      const newIndex = formData.items.findIndex((item) => item.id === over?.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(formData.items, oldIndex, newIndex);
        setFormData(prev => ({ ...prev, items: newItems }));
      }
    }
    
    setActiveId(null);
  };

  const handleSave = () => {
    const { title, items, ...contentFields } = formData;
    // Remove IDs from items before saving
    const itemsWithoutIds = items.map(({ id, ...item }) => item);
    
    onSave({
      title: title,
      content: {
        ...content,
        ...contentFields,
        items: itemsWithoutIds
      }
    });
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">Edit Grid Section</h4>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="grid-title">Section Title</Label>
          <Input
            id="grid-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter section title"
          />
        </div>

        {/* Grid Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="grid-columns">Number of Columns</Label>
            <Select
              value={formData.columns.toString()}
              onValueChange={(value) => handleInputChange('columns', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 Column</SelectItem>
                <SelectItem value="2">2 Columns</SelectItem>
                <SelectItem value="3">3 Columns</SelectItem>
                <SelectItem value="4">4 Columns</SelectItem>
                <SelectItem value="5">5 Columns</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="grid-card-style">Card Style</Label>
            <Select
              value={formData.card_style}
              onValueChange={(value) => handleInputChange('card_style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="bordered">Bordered</SelectItem>
                <SelectItem value="elevated">Elevated</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid Items */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <Label>Grid Items</Label>
            <div className="flex items-center gap-2">
              {formData.items.length > 0 && (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={expandedItems.size === formData.items.length ? collapseAllItems : expandAllItems}
                    className="text-xs"
                  >
                    {expandedItems.size === formData.items.length ? (
                      <>
                        <Minimize className="h-3 w-3 mr-1" />
                        Collapse All
                      </>
                    ) : (
                      <>
                        <Expand className="h-3 w-3 mr-1" />
                        Expand All
                      </>
                    )}
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={formData.items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                {formData.items.map((item, index) => (
                  <SortableGridItem
                    key={item.id}
                    item={item}
                    index={index}
                    onItemChange={handleItemChange}
                    onRemove={() => removeItem(index)}
                    isExpanded={expandedItems.has(item.id)}
                    onToggleExpand={() => toggleItemExpanded(item.id)}
                  />
                ))}
              </SortableContext>
              
              <DragOverlay>
                {activeId ? (
                  <Card className="border border-gray-200 bg-white shadow-lg">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          Grid Item
                        </CardTitle>
                        <Button variant="outline" size="sm" disabled>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>

          {formData.items.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No items added yet. Click "Add Item" to create your first grid item.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridSectionEditor;